import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Input, Pagination } from "antd";
import { Link } from "react-router-dom";
import { useSupplies, mapOrderFromApi } from "../context/SuppliesContext";
import { fetchActiveOrdersAsync } from "../utils/fetch";
import { ExpandedOrderRow } from "./ExpandedOrderRow";

const getPriorityColor = (priority) => {
    if (priority === "Запуск") return "#52c41a";
    if (priority === "Бронь") return "#1677ff";
    if (priority === "Стоп") return "#ff4d4f";
    return "#d9d9d9";
};

const getStatusTag = (status) => {
    if (!status) return null;
    if (status === "Закуплено") return <Tag color="green">{status}</Tag>;
    if (status === "Отгружено") return <Tag color="orange">{status}</Tag>;
    return <Tag>{status}</Tag>;
};

const Purchased = () => {
    const { rawOrders, warehouseStock } = useSupplies();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [searchText, setSearchText] = useState('');

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchActiveOrdersAsync("ALL", currentPage, pageSize, searchText, "PURCHASED,SHIPPED");
            if (result && result.application) {
                const mapped = result.application.map((app, index) => ({
                    ...mapOrderFromApi(app),
                    num: (currentPage - 1) * pageSize + index + 1,
                }));
                setData(mapped);
                setTotalCount(result.totalCount || 0);
            }
        } catch (e) {
            console.error("Ошибка загрузки Purchased:", e);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchText]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // сброс на 1 страницу при поиске
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const columns = [
        {
            title: "Номер договора",
            dataIndex: "contractNum",
            key: "contractNum",
            width: 140,
            render: (text, record) => (
                <Link to={`/client/${record.clientId}`}>{text}</Link>
            ),
        },
        {
            title: "Срок сдачи",
            dataIndex: "deadline",
            key: "deadline",
            width: 100,
            align: "center",
        },
        {
            title: "Приоритет",
            dataIndex: "priority",
            key: "priority",
            width: 90,
            align: "center",
            filters: [
                { text: "Запуск", value: "Запуск" },
                { text: "Бронь", value: "Бронь" },
                { text: "Стоп", value: "Стоп" },
            ],
            onFilter: (value, record) => record.priority === value,
            render: (v) => (
                <span style={{ color: getPriorityColor(v), fontWeight: 600 }}>
                    {v}
                </span>
            ),
        },
        {
            title: "Кол-во дверей",
            dataIndex: "doorCount",
            key: "doorCount",
            width: 100,
            align: "center",
        },
        {
            title: "Алюминий",
            dataIndex: "aluminium",
            key: "aluminium",
            width: 130,
            align: "center",
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 130,
            align: "center",
            filters: [
                { text: "Закуплено", value: "Закуплено" },
                { text: "Отгружено", value: "Отгружено" },
            ],
            onFilter: (value, record) => record.status === value,
            render: getStatusTag,
        },
    ];

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Input.Search
                    placeholder="Поиск по № договора..."
                    allowClear
                    onChange={handleSearchChange}
                    style={{ width: 300 }}
                />
            </div>
            <div style={{ overflowX: "auto" }}>
                <Table
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                    locale={{ emptyText: "Заявки не найдены" }}
                    loading={loading}
                    style={{ minWidth: 700 }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <ExpandedOrderRow
                                record={record}
                                rawOrders={rawOrders}
                                warehouseStock={warehouseStock}
                            />
                        ),
                        rowExpandable: () => true,
                    }}
                />
            </div>

            {totalCount > 0 && (
                <div style={{ marginTop: 16, textAlign: "center" }}>
                    <Pagination
                        current={currentPage}
                        total={totalCount}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        onShowSizeChange={handlePageChange}
                        showTotal={(total) => `Всего: ${total} заявок`}
                    />
                </div>
            )}
        </div>
    );
};

export default Purchased;
